import React from "react";
import { useNavigate } from "react-router-dom";
import { WebPaths } from "../../../../routes/data";
import {
  FaMicrophone,
  FaWandMagicSparkles,
  FaRobot,
  FaArrowRight,
  FaCheck,
} from "react-icons/fa6";
import { motion } from "framer-motion";

import Button from "../../components/ui/Button";

const FiBiSection = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-gray-900 py-24 sm:py-32 overflow-hidden relative">
      {/* Background Gradients */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-purple-900/20 rounded-full blur-[128px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-900/20 rounded-full blur-[128px]" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div className="mx-auto max-w-2xl text-center mb-16">
          {/* <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 rounded-full bg-purple-500/10 px-3 py-1 text-sm font-semibold text-purple-400 ring-1 ring-inset ring-purple-500/20 mb-6"
          >
            <FaRobot className="h-4 w-4" />
            <span>Meet FiBi</span>
          </motion.div> */}
          <h2 className="text-3xl font-light tracking-tight text-white sm:text-5xl font-spartan">
            Meet FiBi, Your Personal <br />
            <span className="text-purple-a8 italic mx-1">
              Interview Coach
            </span>
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-300">
            Don't just memorize answers. Train with FiBi to refine your delivery, fix weak responses, and build the confidence to ace your visa interview.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:grid-rows-2 h-auto lg:h-[600px]">
          {/* Main Feature: Interactive Mock Interview */}
          <div className="lg:col-span-8 lg:row-span-2 relative overflow-hidden rounded-3xl bg-gray-800/50 border border-gray-700/50 p-8 shadow-2xl">
            <div className="relative z-10 h-full flex flex-col">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-12 w-12 rounded-2xl bg-linear-to-br from-purple-500 to-blue-600 flex items-center justify-center text-white text-2xl shadow-lg ring-4 ring-gray-900/50">
                  <FaRobot />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Realistic Mock Interviews
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Voice-enabled simulations that feel like the real thing.
                  </p>
                </div>
              </div>

              {/* Chat UI Mockup */}
              <div className="flex-1 w-full bg-gray-900/80 rounded-2xl border border-gray-700/50 p-6 flex flex-col gap-4 overflow-hidden relative">
                {/* Fibi Message */}
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="self-start max-w-[80%]"
                >
                  <div className="flex items-end gap-2">
                    <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-xs text-white">
                      FiBi
                    </div>
                    <div className="bg-gray-800 p-4 rounded-2xl rounded-bl-sm text-gray-200 text-sm shadow-md border border-gray-700">
                      <p> Why do you want to travel to the United States?</p>
                    </div>
                  </div>
                </motion.div>

                {/* User Message (Voice Wave) */}
                <motion.div
                  initial={{ x: 20, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="self-end max-w-[80%] flex flex-col items-end"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                      Recording...
                    </span>
                    <div className="flex gap-0.5 items-end h-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <motion.div
                          key={i}
                          animate={{ height: [4, 12, 4] }}
                          transition={{
                            repeat: Infinity,
                            duration: 0.5,
                            delay: i * 0.1,
                          }}
                          className="w-1 bg-red-500 rounded-full"
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="bg-blue-600 p-4 rounded-2xl rounded-br-sm text-white text-sm shadow-md">
                      <p>To pursue my Master's in Computer Science at...</p>
                    </div>
                    <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-white">
                      You
                    </div>
                  </div>
                </motion.div>

                {/* Fibi Feedback */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="self-start max-w-[90%] mt-2"
                >
                  <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-xl p-4 flex gap-3">
                    <FaCheck className="text-emerald-400 mt-1 shrink-0" />
                    <div>
                      <p className="text-emerald-200 text-sm font-semibold mb-1">
                        Great start!
                      </p>
                      <p className="text-emerald-400/80 text-xs text-pretty">
                        Try mentioning specific course modules to show deeper
                        intent.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-purple-500/20 blur-[100px] z-0 pointer-events-none" />
          </div>

          {/* Feature 2: Smart Correction */}
          <div className="lg:col-span-4 lg:row-span-1 relative overflow-hidden rounded-3xl bg-gray-800/50 border border-gray-700/50 p-6 flex flex-col shadow-xl group hover:border-purple-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-orange-500/20 text-orange-400 flex items-center justify-center text-lg">
                <FaWandMagicSparkles />
              </div>
              <h3 className="text-lg font-bold text-white">
                Smart Enhancement
              </h3>
            </div>

            <div className="flex-1 flex flex-col gap-3 relative z-10">
              {/* Weak Answer */}
              <div className="bg-red-900/20 border border-red-500/20 p-3 rounded-lg opacity-60 scale-95 origin-left">
                <div className="flex justify-between text-[10px] text-red-300 mb-1 uppercase tracking-wide">
                  <span>Weak Response</span>
                  <span>Score: 4/10</span>
                </div>
                <div className="h-2 w-3/4 bg-red-500/20 rounded mb-1" />
                <div className="h-2 w-1/2 bg-red-500/20 rounded" />
              </div>

              {/* Arrow */}
              <div className="absolute left-6 top-1/2 -translate-y-1/2 z-20 bg-gray-800 p-1 rounded-full border border-gray-700">
                <FaArrowRight className="text-gray-500 w-3 h-3" />
              </div>

              {/* Strong Answer */}
              <div className="bg-emerald-900/20 border border-emerald-500/20 p-3 rounded-lg shadow-lg scale-105 origin-left mt-auto">
                <div className="flex justify-between text-[10px] text-emerald-300 mb-1 uppercase tracking-wide">
                  <span>FiBi's Suggestion</span>
                  <span>Score: 9.5/10</span>
                </div>
                <div className="h-2 w-full bg-emerald-500/20 rounded mb-1" />
                <div className="h-2 w-3/4 bg-emerald-500/20 rounded mb-1" />
                <div className="h-2 w-5/6 bg-emerald-500/20 rounded" />
              </div>
            </div>
          </div>

          {/* Feature 3: Voice Analysis */}
          <div className="lg:col-span-4 lg:row-span-1 relative overflow-hidden rounded-3xl bg-gray-800/50 border border-gray-700/50 p-6 flex flex-col shadow-xl group hover:border-blue-500/30 transition-colors">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center text-lg">
                <FaMicrophone />
              </div>
              <h3 className="text-lg font-bold text-white">
                Confidence Analysis
              </h3>
            </div>

            <div className="flex-1 flex items-end gap-2 justify-center pb-4">
              {[40, 70, 50, 90, 60, 80, 45, 75, 55, 60].map((h, i) => (
                <motion.div
                  key={i}
                  animate={{ height: [`${h / 2}%`, `${h}%`, `${h / 2}%`] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                  className="w-1.5 bg-linear-to-t from-blue-600 to-purple-500 rounded-full"
                />
              ))}
            </div>
            <p className="text-center text-xs text-gray-400 font-medium">
              Detects hesitation, pace, and clarity.
            </p>
          </div>
        </div>

        <div className="mt-16 flex justify-center">
          <Button
            label="Try FiBi Interview"
            onClick={() => navigate(WebPaths.FiBiAi)}
            variant="fibi"
            size="large"
            endIcon={<FaArrowRight className="h-4 w-4" />}
            iconHoverStyle="scale"
          />
        </div>
      </div>
    </section>
  );
};

export default FiBiSection;
